import { api } from '@/lib/interceptor';
import type { AxiosResponse, AxiosError, AxiosRequestConfig } from 'axios';

type Config = {
    headers?: Record<string, string>;
    data?: Record<string, unknown> | FormData | string;
};

async function request<T>(fn: () => Promise<AxiosResponse<T>>, delay = 200): Promise<T> {
    try {
        const start = Date.now();
        const response = await fn();
        const elapsed = Date.now() - start;
        if (delay > 0 && elapsed < delay) {
            await new Promise(resolve => {
                setTimeout(resolve, delay - elapsed);
            });
        }
        return response.data;
    } catch (error: unknown) {
        if (error && typeof error === 'object' && 'response' in error) {
            throw (error as AxiosError).response;
        }
        throw error;
    }
}

function setConfig(contentType?: string): Config {
    if (contentType === 'multipart/form-data') {
        return {};
    }

    return {
        headers: {
            'Content-Type': contentType ?? 'application/json'
        }
    };
}

function bodyHandler<T>(body: T, contentType?: string): T | FormData | string {
    if (!body) return body;

    if (contentType === 'multipart/form-data') {
        if (body instanceof FormData) return body;

        const formData = new FormData();

        Object.entries(body as Record<string, unknown>).forEach(([key, value]) => {
            if (value === undefined || value === null) return;

            if (typeof value === 'boolean') {
                formData.append(key, value ? 'true' : 'false');
            } else if (value instanceof File || value instanceof Blob) {
                formData.append(key, value);
            } else if (Array.isArray(value) || typeof value === 'object') {
                formData.append(key, JSON.stringify(value));
            } else {
                formData.append(key, String(value));
            }
        });

        return formData;
    }

    if (contentType === 'application/json') {
        return JSON.stringify(body);
    }

    return body;
}

function get<T = unknown>(url: string, delay?: number, config?: AxiosRequestConfig): Promise<T> {
    return request<T>(() => {
        return api.get<T>(url, config);
    }, delay);
}

function del<T = unknown, B = unknown>(
    url: string,
    body?: B,
    delay?: number,
    contentType: string = 'application/json'
): Promise<T> {
    return request<T>(() => {
        const config: Config = setConfig(contentType);
        if (body) {
            config.data = bodyHandler<B>(body, contentType) as Config['data'];
        }
        return api.delete<T>(url, config);
    }, delay);
}

function post<T = unknown, B = unknown>(
    url: string,
    body: B,
    delay: number,
    contentType: string = 'application/json'
): Promise<T> {
    return request<T>(() => {
        return api.post<T>(url, bodyHandler<B>(body, contentType), setConfig(contentType));
    }, delay);
}

function put<T = unknown, B = unknown>(
    url: string,
    body: B,
    delay: number,
    contentType: string = 'application/json'
): Promise<T> {
    return request<T>(() => {
        return api.put<T>(url, bodyHandler<B>(body, contentType), setConfig(contentType));
    }, delay);
}

function patch<T = unknown, B = unknown>(
    url: string,
    body: B,
    delay: number,
    contentType: string = 'application/json'
): Promise<T> {
    return request<T>(() => {
        return api.patch<T>(url, bodyHandler<B>(body, contentType), setConfig(contentType));
    }, delay);
}

export const axiosWrapper = {
    get,
    del,
    post,
    put,
    patch
};
